import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { TokenPair } from '@expeditor/shared';
import { useAuthStore } from '../auth/auth.store';

/**
 * HTTP-клиент с авто-вшиванием Bearer-токена и единичным retry при 401.
 *
 * При 401 на любом запросе:
 *  1. Делается ОДНА попытка обновить пару через POST /auth/refresh с текущим refreshToken.
 *  2. При успехе — токены сохраняются в store, оригинальный запрос повторяется.
 *  3. При неудаче — store очищается, запрос проваливается, страница reload'ится
 *     (роутер перебросит на /login).
 *
 * Несколько одновременных 401 шарят одну попытку рефреша через `refreshPromise`.
 */
export const apiClient = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Прокидываем X-Timezone для корректной фиксации в compliance-журналах.
apiClient.interceptors.request.use((config) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  config.headers['X-Timezone'] = tz;
  const access = useAuthStore.getState().accessToken;
  if (access && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

let refreshPromise: Promise<TokenPair | null> | null = null;

async function tryRefresh(): Promise<TokenPair | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    const res = await axios.post<TokenPair>('/api/auth/refresh', { refreshToken });
    useAuthStore.getState().setTokens(res.data);
    return res.data;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.endsWith('/auth/refresh') &&
      !original.url?.endsWith('/auth/login')
    ) {
      original._retry = true;
      refreshPromise ??= tryRefresh();
      const fresh = await refreshPromise;
      refreshPromise = null;
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh.accessToken}`;
        return apiClient.request(original);
      }
      // Если рефреш не удался — пусть страница перезагрузится и роутер выкинет на /login.
      if (typeof window !== 'undefined') window.location.href = '/login';
    }
    // Подменяем невнятное «Request failed with status code 400» текстом сервера:
    // экраны показывают error.message, а полезное лежит в теле ответа.
    const data = error.response?.data as
      | { message?: string; errors?: { path?: string; message?: string }[] }
      | undefined;
    if (data?.message) {
      const details = (data.errors ?? [])
        .map((e) => [e.path, e.message].filter(Boolean).join(': '))
        .filter(Boolean)
        .join('; ');
      error.message = details ? `${data.message}: ${details}` : data.message;
    }
    return Promise.reject(error);
  },
);
